/**
 * Service-layer wrappers around `/v1/golab/annotation/...` (B5).
 *
 * Each function calls `fetchWithAuth` and returns the decoded JSON shape
 * verbatim (no transformation). Errors propagate as `APIErrorException`
 * from `fetchWithAuth` — consumers (the react-query hooks in
 * `../hooks/useAnnotation.ts`) handle them.
 */

import { fetchWithAuth } from '../../../../../utils/api';
import type {
  CompletedTodayResponse,
  DatasetRecordDetail,
  ListPaginationParams,
  PipelineMetricsResponse,
  QueueList,
  SubmitAnnotationPayload,
  SubmitAnnotationResult,
  SubmitResolutionPayload,
  SubmitResolutionResult,
} from '../types';

const BASE_PATH = '/v1/golab/annotation';

/**
 * Build `?limit=&offset=` from `ListPaginationParams`. Uses `!= null` so
 * `offset: 0` still gets sent (a truthy check would drop it).
 */
const buildPaginationQuery = (params?: ListPaginationParams): string => {
  const sp = new URLSearchParams();
  if (params?.limit != null) sp.append('limit', String(params.limit));
  if (params?.offset != null) sp.append('offset', String(params.offset));
  const q = sp.toString();
  return q ? `?${q}` : '';
};

/** The current user's annotation queue per `feature-spec.md` §3.3. */
export const getMyQueue = async (
  params?: ListPaginationParams,
): Promise<QueueList> =>
  fetchWithAuth(`${BASE_PATH}/queue${buildPaginationQuery(params)}`);

/**
 * Pure pass-through GET for the annotation pipeline counts widget (B18-FE).
 * The BE (B18-BE) returns all 5 buckets keyed by camelCase; the FE
 * consumes the response unchanged.
 */
export const getPipelineMetrics = async (): Promise<PipelineMetricsResponse> =>
  fetchWithAuth(`${BASE_PATH}/pipeline-metrics`);

/**
 * Pure pass-through GET for the "Completed today" widget (B20-FE).
 * The BE (B20-BE) returns `{annotationSubmissions, finalisations}`
 * keyed by camelCase; the FE consumes the response unchanged.
 *
 * Forwards the browser's IANA timezone so "today" matches the user's
 * intuition (midnight in their local TZ, not UTC midnight). BE falls
 * back to UTC if the param is missing or unrecognised, so the call
 * still works if `Intl.DateTimeFormat` is unavailable in some unusual
 * runtime.
 */
export const getCompletedToday = async (): Promise<CompletedTodayResponse> => {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // jsdom or very old browsers may not implement Intl fully; fall
    // through to BE's UTC default rather than failing the request.
    tz = undefined;
  }
  const qs = tz ? `?tz=${encodeURIComponent(tz)}` : '';
  return fetchWithAuth(`${BASE_PATH}/completed-today${qs}`);
};

/**
 * Mixed agreed + disputed records in the org (B19-BE). Powers the review
 * queue at `/v4/go-lab/annotate/review`. Replaces the prior
 * `/adjudication-queue` endpoint (now 404).
 */
export const getReviewQueue = async (
  params?: ListPaginationParams,
): Promise<QueueList> =>
  fetchWithAuth(
    `${BASE_PATH}/review-queue${buildPaginationQuery(params)}`,
  );

/** Full record detail with `is_current` first + second + resolution. */
export const getDatasetRecord = async (
  recordId: string,
): Promise<DatasetRecordDetail> =>
  fetchWithAuth(`${BASE_PATH}/records/${recordId}`);

/**
 * Submit a first or second annotator pass. Server response includes the
 * `agreement` object only when this was the second pass.
 */
export const submitAnnotation = async (
  recordId: string,
  payload: SubmitAnnotationPayload,
): Promise<SubmitAnnotationResult> =>
  fetchWithAuth(`${BASE_PATH}/records/${recordId}/annotations`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/**
 * Resolve a disputed record. Promotes it to `finalised` server-side with
 * `goldLabels = labels`. 400 if rationale is empty or record isn't disputed.
 */
export const submitResolution = async (
  recordId: string,
  payload: SubmitResolutionPayload,
): Promise<SubmitResolutionResult> =>
  fetchWithAuth(`${BASE_PATH}/records/${recordId}/resolution`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/**
 * Self-claim the next available record for annotation (B14-BE).
 *
 * The BE picks the oldest claimable record in the caller's org: prefers
 * second-pass-eligible (status `annotated_first`, caller is NOT the first
 * annotator) over first-pass-eligible (status `pending`), oldest-first
 * within each tier. On a successful claim the record's `first_annotator_id`
 * or `second_annotator_id` is set to the caller and its status flips to
 * `assigned_first` / `assigned_second` respectively.
 *
 * Returns `null` when no record is claimable (BE responds 204; the
 * `null` result is produced by `fetchWithAuth`'s 204 handler).
 */
export const startNextAvailable = async (): Promise<DatasetRecordDetail | null> =>
  fetchWithAuth(`${BASE_PATH}/records/next/start`, { method: 'POST' });

/**
 * Explicitly self-claim a specific record. Used by the AnnotationFlowPage's
 * "Claim & start annotating" gate when the user arrives at a record they
 * don't yet own. Same state-machine rules as `startNextAvailable`:
 *
 *   * `pending` → caller becomes `first_annotator_id` (status → assigned_first).
 *   * `annotated_first` (no second annotator, caller != first) → caller
 *     becomes `second_annotator_id` (status → assigned_second).
 *
 * Throws on 400 (record already claimed / wrong state / same-user) and 404
 * (unknown / cross-org).
 */
export const startAnnotation = async (
  recordId: string,
): Promise<DatasetRecordDetail> =>
  fetchWithAuth(`${BASE_PATH}/records/${recordId}/start`, { method: 'POST' });

/**
 * Finalise an `agreed` record straight to the golden dataset (B19-BE).
 * Server promotes status `agreed` → `finalised`. No body. Returns the
 * updated `DatasetRecordDetail`. Throws on 400 (wrong status) and 404
 * (unknown / cross-org).
 */
export const finaliseRecord = async (
  recordId: string,
): Promise<DatasetRecordDetail> =>
  fetchWithAuth(`${BASE_PATH}/records/${recordId}/finalise`, { method: 'POST' });


/**
 * Aggregator export — matches the dual-export convention used by
 * `datasetsService.ts`. Consumers can import either bare functions
 * (`import { getMyQueue } from '...'`) or the namespace
 * (`import { annotationService } from '...'; annotationService.getMyQueue(...)`).
 */
export const annotationService = {
  getMyQueue,
  getReviewQueue,
  getDatasetRecord,
  submitAnnotation,
  submitResolution,
  startNextAvailable,
  startAnnotation,
  finaliseRecord,
  getPipelineMetrics,
  getCompletedToday,
};
